import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getApiUrl } from '@/lib/subdomain'

const apiUrl = getApiUrl()

export function AdminAccounts() {
  const queryClient = useQueryClient()
  
  const { data: accounts, isLoading } = useQuery({
    queryKey: ['admin', 'accounts'],
    queryFn: async () => {
      const res = await fetch(`${apiUrl}/admin/accounts`, { credentials: 'include' })
      if (!res.ok) throw new Error('Failed to fetch accounts')
      return res.json()
    },
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    )
  }

  const steamAccount = accounts?.accounts?.find((a: any) => a.platform === 'steam')
  const epicAccount = accounts?.accounts?.find((a: any) => a.platform === 'epic')
  const xboxAccount = accounts?.accounts?.find((a: any) => a.platform === 'xbox')

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-display font-bold mb-2">Gaming Accounts</h1>
        <p className="text-gray-400">Connect your gaming accounts to display stats on your site</p>
      </div>

      <div className="grid gap-6">
        <SteamAccountCard 
          account={steamAccount} 
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['admin', 'accounts'] })}
        />
        <FortniteAccountCard 
          account={epicAccount}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['admin', 'accounts'] })}
        />
        <XboxAccountCard 
          account={xboxAccount}
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['admin', 'accounts'] })}
        />
      </div>
    </div>
  )
}

function AccountCardWrapper({ 
  children, 
  platform, 
  icon, 
  color, 
  connected, 
  username 
}: { 
  children: React.ReactNode
  platform: string
  icon: string
  color: string
  connected: boolean
  username?: string
}) {
  return (
    <div className="rounded-2xl border border-gray-800 overflow-hidden bg-gray-900/30">
      {/* Header */}
      <div className={`p-5 ${color} flex items-center gap-4`}>
        <div className="w-14 h-14 rounded-xl bg-black/20 flex items-center justify-center text-3xl">
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-white">{platform}</h3>
          {connected ? (
            <div className="flex items-center gap-2 text-white/80">
              <span className="w-2 h-2 bg-green-400 rounded-full" />
              <span>Connected as {username}</span>
            </div>
          ) : (
            <span className="text-white/60">Not connected</span>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-5">
        {children}
      </div>
    </div>
  )
}

function SteamAccountCard({ account, onUpdate }: { account: any; onUpdate: () => void }) {
  const [steamId, setSteamId] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  const linkMutation = useMutation({
    mutationFn: async (steamId: string) => {
      const res = await fetch(`${apiUrl}/admin/accounts/steam`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ steamId }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to link account')
      return data
    },
    onSuccess: () => {
      setSteamId('')
      setError('')
      setSuccess('Steam account linked successfully!')
      onUpdate()
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: Error) => setError(err.message),
  })
  
  const unlinkMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiUrl}/admin/accounts/steam`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to unlink account')
    },
    onSuccess: onUpdate,
  })
  
  const refreshMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiUrl}/admin/accounts/steam/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to refresh stats')
      return res.json()
    },
    onSuccess: () => {
      setSuccess('Stats refreshed!')
      onUpdate()
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: Error) => setError(err.message),
  })

  return (
    <AccountCardWrapper
      platform="Steam"
      icon="🎮"
      color="bg-gradient-to-r from-[#1b2838] to-[#2a475e]"
      connected={!!account}
      username={account?.platform_username}
    >
      {account ? (
        <div className="flex flex-wrap gap-3">
          <button 
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            className="btn bg-primary-500/20 hover:bg-primary-500/30 text-primary-400"
          >
            {refreshMutation.isPending ? (
              <><span className="animate-spin mr-2">⟳</span> Refreshing...</>
            ) : (
              <><span className="mr-2">🔄</span> Refresh Stats</>
            )}
          </button>
          <button 
            onClick={() => unlinkMutation.mutate()}
            disabled={unlinkMutation.isPending}
            className="btn bg-red-500/10 hover:bg-red-500/20 text-red-400"
          >
            <span className="mr-2">🗑️</span> Unlink Account
          </button>
          {success && <span className="text-green-400 flex items-center">{success}</span>}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Steam ID (64-bit)</label>
            <input
              type="text"
              value={steamId}
              onChange={(e) => setSteamId(e.target.value)}
              placeholder="76561198000000000"
              className="input w-full"
            />
            <p className="text-xs text-gray-500 mt-2">
              Find your Steam ID at{' '}
              <a href="https://steamid.io" target="_blank" rel="noopener" className="text-primary-400 hover:underline">
                steamid.io
              </a>
            </p>
          </div>
          
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
              {error}
            </div>
          )}
          
          <button
            onClick={() => linkMutation.mutate(steamId)}
            disabled={!steamId || linkMutation.isPending}
            className="btn btn-primary"
          >
            {linkMutation.isPending ? (
              <><span className="animate-spin mr-2">⟳</span> Linking...</>
            ) : (
              <><span className="mr-2">🔗</span> Link Steam Account</>
            )}
          </button>
        </div>
      )}
    </AccountCardWrapper>
  )
}

function FortniteAccountCard({ account, onUpdate }: { account: any; onUpdate: () => void }) {
  const [epicUsername, setEpicUsername] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  const linkMutation = useMutation({
    mutationFn: async (epicUsername: string) => {
      const res = await fetch(`${apiUrl}/admin/accounts/fortnite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ epicUsername }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to link account')
      return data
    },
    onSuccess: () => {
      setEpicUsername('')
      setError('')
      setSuccess('Fortnite account linked!')
      onUpdate()
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: Error) => setError(err.message),
  })
  
  const unlinkMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiUrl}/admin/accounts/fortnite`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to unlink account')
    },
    onSuccess: onUpdate,
  })
  
  const refreshMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiUrl}/admin/accounts/fortnite/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to refresh stats')
      return res.json()
    },
    onSuccess: () => {
      setSuccess('Stats refreshed!')
      onUpdate()
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: Error) => setError(err.message),
  })

  return (
    <AccountCardWrapper
      platform="Fortnite"
      icon="🎯"
      color="bg-gradient-to-r from-[#9d4dbb] to-[#5c3d7a]"
      connected={!!account}
      username={account?.platform_username}
    >
      {account ? (
        <div className="flex flex-wrap gap-3">
          <button 
            onClick={() => refreshMutation.mutate()}
            disabled={refreshMutation.isPending}
            className="btn bg-primary-500/20 hover:bg-primary-500/30 text-primary-400"
          >
            {refreshMutation.isPending ? (
              <><span className="animate-spin mr-2">⟳</span> Refreshing...</>
            ) : (
              <><span className="mr-2">🔄</span> Refresh Stats</>
            )}
          </button>
          <button 
            onClick={() => unlinkMutation.mutate()}
            disabled={unlinkMutation.isPending}
            className="btn bg-red-500/10 hover:bg-red-500/20 text-red-400"
          >
            <span className="mr-2">🗑️</span> Unlink Account
          </button>
          {success && <span className="text-green-400 flex items-center">{success}</span>}
          {error && <span className="text-red-400 flex items-center">{error}</span>}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Epic Games Username</label>
            <input
              type="text"
              value={epicUsername}
              onChange={(e) => setEpicUsername(e.target.value)}
              placeholder="Your Epic username"
              className="input w-full"
            />
            <p className="text-xs text-gray-500 mt-2">
              Make sure your Fortnite stats are set to <strong>public</strong>
            </p>
          </div>
          
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
              {error}
            </div>
          )}
          
          <button
            onClick={() => linkMutation.mutate(epicUsername)}
            disabled={!epicUsername || linkMutation.isPending}
            className="btn btn-primary"
          >
            {linkMutation.isPending ? (
              <><span className="animate-spin mr-2">⟳</span> Linking...</>
            ) : (
              <><span className="mr-2">🔗</span> Link Fortnite Account</>
            )}
          </button>
        </div>
      )}
    </AccountCardWrapper>
  )
}

function XboxAccountCard({ account, onUpdate }: { account: any; onUpdate: () => void }) {
  const [gamertag, setGamertag] = useState('')
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  
  const linkMutation = useMutation({
    mutationFn: async (gamertag: string) => {
      const res = await fetch(`${apiUrl}/admin/accounts/xbox`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ gamertag }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to link account')
      return data
    },
    onSuccess: () => {
      setGamertag('')
      setError('')
      setSuccess('Xbox account linked!')
      onUpdate()
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: Error) => setError(err.message),
  })
  
  const unlinkMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${apiUrl}/admin/accounts/xbox`, {
        method: 'DELETE',
        credentials: 'include',
      })
      if (!res.ok) throw new Error('Failed to unlink account')
    },
    onSuccess: onUpdate,
  })

  return (
    <AccountCardWrapper
      platform="Xbox"
      icon="🎮"
      color="bg-gradient-to-r from-[#107c10] to-[#0e5c0e]"
      connected={!!account}
      username={account?.platform_username}
    >
      {account ? (
        <div className="flex flex-wrap gap-3">
          <button 
            onClick={() => unlinkMutation.mutate()}
            disabled={unlinkMutation.isPending}
            className="btn bg-red-500/10 hover:bg-red-500/20 text-red-400"
          >
            <span className="mr-2">🗑️</span> Unlink Account
          </button>
          {success && <span className="text-green-400 flex items-center">{success}</span>}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Xbox Gamertag</label>
            <input
              type="text"
              value={gamertag}
              onChange={(e) => setGamertag(e.target.value)}
              placeholder="Your Xbox gamertag"
              className="input w-full"
            />
            <p className="text-xs text-gray-500 mt-2">
              Note: Full Xbox stats require additional API setup
            </p>
          </div>
          
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
              {error}
            </div>
          )}
          
          <button
            onClick={() => linkMutation.mutate(gamertag)}
            disabled={!gamertag || linkMutation.isPending}
            className="btn btn-primary"
          >
            {linkMutation.isPending ? (
              <><span className="animate-spin mr-2">⟳</span> Linking...</>
            ) : (
              <><span className="mr-2">🔗</span> Link Xbox Account</>
            )}
          </button>
        </div>
      )}
    </AccountCardWrapper>
  )
}
